import { LegalPageLayout, LegalSection } from '../components/LegalPageLayout';
import { LEGAL_SERVICE } from '../legal.constants';

const PROCESSING_SUMMARY = [
  {
    data: 'Identité, coordonnées, compte, rôles et accès',
    purpose: 'Créer le compte, authentifier et attribuer les droits',
    legalBasis:
      'Exécution du contrat avec le client et intérêt légitime à sécuriser le service',
    retention:
      'Pendant l’accès au service, puis pendant les délais nécessaires à la gestion du contrat et des contestations',
  },
  {
    data: 'Dossiers d’entreprise, identité professionnelle, emploi, rémunération, absences, paie, BDESE et documents',
    purpose: 'Exécuter les services demandés par l’employeur',
    legalBasis: 'Instructions du client et obligations de l’employeur',
    retention:
      'Selon les instructions documentées du client et les durées légales propres aux documents concernés',
  },
  {
    data: 'Journaux de connexion, d’action et d’erreur',
    purpose:
      'Sécuriser les accès, diagnostiquer et établir la trace des opérations',
    legalBasis: 'Intérêt légitime à sécuriser et administrer le service',
    retention:
      'Durée proportionnée au besoin de sécurité et de preuve, puis suppression ou archivage restreint',
  },
  {
    data: 'Facturation, paiements et demandes d’assistance',
    purpose: 'Facturer, encaisser, répondre et conserver les justificatifs',
    legalBasis: 'Exécution du contrat et obligations légales',
    retention:
      'Pendant la relation, puis pendant les durées comptables, fiscales ou contentieuses applicables',
  },
  {
    data: 'Paramètres de messagerie, identifiants techniques chiffrés et jetons OAuth',
    purpose:
      'Connecter OpenPaye ou l’expéditeur choisi et exécuter les envois demandés',
    legalBasis: 'Exécution du service demandé par le client',
    retention:
      'Pendant l’activation de la connexion, puis suppression ou révocation selon la demande et les obligations applicables',
  },
] as const;

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Politique de confidentialité">
      <p className="text-lg leading-8 text-neutral-800">
        Cette page décrit les traitements visibles dans l&apos;application. Elle
        distingue les données gérées par l&apos;éditeur pour faire fonctionner
        le service des données RH et de paie confiées par un employeur.
      </p>

      <LegalSection title="Qui est responsable ?">
        <p>
          Pour la gestion des comptes, de la sécurité, du support et de la
          facturation du service, {LEGAL_SERVICE.publisher}, éditeur
          d&apos;Innov&apos;Paie, agit comme responsable du traitement.
        </p>
        <p>
          Pour les dossiers salariés, bulletins, absences, variables de paie,
          documents et données BDESE chargés par une entreprise cliente,
          l&apos;employeur reste responsable du traitement. L&apos;éditeur du
          service intervient alors comme sous-traitant, dans le périmètre fixé
          par le client et son contrat.
        </p>
      </LegalSection>

      <LegalSection title="Données, usages et conservation">
        <div className="shadow-soft hidden overflow-x-auto rounded-xl bg-white sm:block">
          <table
            aria-label="Données traitées, finalités, bases et durées"
            className="w-full min-w-[42rem] border-collapse text-left text-sm"
          >
            <thead>
              <tr className="border-b border-neutral-200 text-neutral-950">
                <th className="px-4 py-3 font-semibold">Données</th>
                <th className="px-4 py-3 font-semibold">Pourquoi</th>
                <th className="px-4 py-3 font-semibold">Base</th>
                <th className="px-4 py-3 font-semibold">Durée ou critère</th>
              </tr>
            </thead>
            <tbody className="align-top">
              {PROCESSING_SUMMARY.map((item, index) => (
                <tr
                  key={item.data}
                  className={
                    index < PROCESSING_SUMMARY.length - 1
                      ? 'border-b border-neutral-100'
                      : undefined
                  }
                >
                  <td className="px-4 py-3">{item.data}</td>
                  <td className="px-4 py-3">{item.purpose}</td>
                  <td className="px-4 py-3">{item.legalBasis}</td>
                  <td className="px-4 py-3">{item.retention}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ol
          aria-label="Données traitées, finalités, bases et durées"
          className="space-y-4 sm:hidden"
        >
          {PROCESSING_SUMMARY.map(item => (
            <li
              key={item.data}
              className="rounded-xl border border-neutral-200 bg-white p-4"
            >
              <h3 className="leading-6 font-semibold text-neutral-950">
                {item.data}
              </h3>
              <dl className="mt-3 space-y-3 text-sm leading-6">
                <div>
                  <dt className="font-medium text-neutral-500">Pourquoi</dt>
                  <dd className="text-neutral-800">{item.purpose}</dd>
                </div>
                <div>
                  <dt className="font-medium text-neutral-500">Base</dt>
                  <dd className="text-neutral-800">{item.legalBasis}</dd>
                </div>
                <div>
                  <dt className="font-medium text-neutral-500">
                    Durée ou critère
                  </dt>
                  <dd className="text-neutral-800">{item.retention}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ol>
        <p className="text-sm text-neutral-600">
          La durée exacte d&apos;un dossier RH dépend du type de document et des
          instructions de l&apos;employeur. Cette page ne promet donc pas une
          suppression automatique uniforme après la fermeture d&apos;un compte.
        </p>
        <p className="text-sm text-neutral-600">
          Une absence ou un document RH peut révéler indirectement une donnée
          sensible, notamment de santé. Le service ne demande pas d&apos;ajouter
          un diagnostic médical dans une demande d&apos;absence ;
          l&apos;employeur définit les justificatifs strictement nécessaires à
          sa gestion.
        </p>
      </LegalSection>

      <LegalSection title="Origine des données et automatisations">
        <p>
          Les données proviennent de la personne concernée, de son employeur ou
          de son cabinet, d&apos;une synchronisation OpenPaye demandée par le
          client, ou sont produites par le service sous forme de calculs,
          journaux, notifications et documents. Elles ne sont pas collectées
          depuis des sources publiques.
        </p>
        <p>
          Le service automatise des calculs et des tâches techniques, mais ne
          prend pas seul une décision produisant des effets juridiques sur un
          salarié. Une approbation d&apos;absence et les décisions de paie
          restent sous le contrôle des personnes habilitées chez
          l&apos;employeur.
        </p>
      </LegalSection>

      <LegalSection title="Données nécessaires ou facultatives">
        <p>
          Les champs signalés comme obligatoires sont nécessaires pour créer un
          compte, exécuter l&apos;action demandée ou respecter une obligation
          liée au service. Sans ces données, l&apos;action concernée ne peut pas
          être terminée. Les autres champs sont facultatifs et leur absence
          n&apos;empêche pas d&apos;utiliser les fonctions qui n&apos;en
          dépendent pas.
        </p>
      </LegalSection>

      <LegalSection title="Destinataires et prestataires">
        <p>
          Les données sont accessibles uniquement aux utilisateurs habilités et
          aux personnes autorisées qui exploitent ou assistent le service. Un
          courriel ou un document n&apos;est communiqué qu&apos;au destinataire
          choisi par l&apos;utilisateur ou prévu par la fonction activée.
        </p>
        <details className="rounded-xl border border-neutral-200 bg-white p-4">
          <summary className="cursor-pointer font-medium text-neutral-950">
            Voir les prestataires selon la fonction utilisée
          </summary>
          <ul className="mt-4 list-disc space-y-2 pl-5">
            <li>l&apos;opérateur de l&apos;infrastructure du service ;</li>
            <li>
              OpenPaye pour les connexions et synchronisations demandées ;
            </li>
            <li>AWS S3 pour le stockage des documents du coffre ;</li>
            <li>Stripe pour la facturation et les paiements ;</li>
            <li>Resend pour les courriels transactionnels du service ;</li>
            <li>
              Google Cloud France (Vertex AI, région Union européenne) pour
              l&apos;assistant en langage naturel, lorsqu&apos;un utilisateur
              l&apos;active dans son cabinet ;
            </li>
            <li>
              le serveur SMTP, Google ou Microsoft choisi par le client pour
              envoyer avec sa propre messagerie.
            </li>
          </ul>
        </details>
      </LegalSection>

      <LegalSection title="Assistant en langage naturel">
        <p>
          L&apos;assistant permet d&apos;écrire en français ce que l&apos;on
          ferait dans OpenPaye ou dans Innov&apos;Paie (« quels dossiers
          n&apos;ont pas leur DSN ? »). Le texte saisi, le nom du dossier
          ouvert et les derniers échanges de la même conversation sur ce
          dossier sont transmis à un modèle de langage (Gemini de Google)
          exécuté par Google Cloud France dans une région de l&apos;Union
          européenne (Belgique, europe-west1). L&apos;application refuse tout
          appel qui ne passerait pas par cette région. Les résultats de paie
          (bulletins, montants, listes de salariés) ne sont jamais transmis au
          modèle : il choisit une action, et c&apos;est Innov&apos;Paie qui
          l&apos;exécute sous les droits de l&apos;utilisateur.
        </p>
        <p>
          Les contenus transmis ne sont pas utilisés pour entraîner les modèles
          (Google Cloud, Service Specific Terms, « Training Restriction »). Ils
          peuvent être conservés jusqu&apos;à trente jours par Google Cloud,
          dans la même région, aux seules fins de détection des usages abusifs,
          tant qu&apos;Innov&apos;Paie n&apos;a pas obtenu l&apos;exemption de
          cette journalisation ; et mis en cache jusqu&apos;à vingt-quatre
          heures dans la même région, sauf désactivation au niveau du projet.
          Innov&apos;Paie conserve le texte des demandes trente jours et la
          trace de l&apos;appel (date, outil choisi, coût, durée) douze mois,
          pour mesurer l&apos;usage et répondre aux demandes ; les journaux
          techniques ne contiennent que l&apos;hôte contacté, le modèle, le
          statut et la durée — jamais le contenu.
        </p>
      </LegalSection>

      <LegalSection title="Transferts hors de l’Espace économique européen">
        <p>
          Resend et Stripe, ainsi que certains de leurs sous-traitants, peuvent
          traiter des données aux États-Unis ou dans d&apos;autres pays. Leurs
          accords de traitement prévoient, selon le flux concerné, le cadre de
          protection des données UE–États-Unis et/ou les clauses contractuelles
          types de la Commission européenne. AWS S3 est configuré dans une
          région européenne, mais ses opérations de support et certains
          sous-traitants peuvent aussi impliquer un accès international encadré
          par son accord de traitement. Le fournisseur de messagerie choisi par
          le client peut ajouter ses propres transferts.
        </p>
        <p>
          Pour l&apos;assistant en langage naturel, Innov&apos;Paie ne recourt
          à aucun sous-traitant établi en dehors de l&apos;Union européenne :
          Google Cloud France est une société établie en France. Elle
          appartient à un groupe dont la société mère est établie aux
          États-Unis : un accès depuis un pays tiers, pour une opération de
          support ou en réponse à une demande d&apos;une autorité étrangère,
          ne peut pas être exclu. Un tel accès constituerait un transfert au
          sens du chapitre V du RGPD ; il est encadré par les clauses
          contractuelles types de la Commission européenne figurant au Cloud
          Data Processing Addendum de Google Cloud. L&apos;obtention du jeton
          d&apos;accès technique (authentification du compte de service, sans
          donnée de paie ni donnée personnelle de salarié) passe par le point
          d&apos;entrée mondial de Google.
        </p>
        <details className="rounded-xl border border-neutral-200 bg-white p-4">
          <summary className="cursor-pointer font-medium text-neutral-950">
            Consulter les garanties et listes de sous-traitants
          </summary>
          <ul className="mt-4 list-disc space-y-2 pl-5">
            <li>
              <a
                href="https://resend.com/legal/dpa"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                Accord de traitement Resend
              </a>{' '}
              et{' '}
              <a
                href="https://resend.com/legal/subprocessors"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                sous-traitants Resend
              </a>
            </li>
            <li>
              <a
                href="https://stripe.com/legal/dpa"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                Accord de traitement Stripe
              </a>{' '}
              et{' '}
              <a
                href="https://stripe.com/legal/service-providers"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                prestataires Stripe
              </a>
            </li>
            <li>
              <a
                href="https://aws.amazon.com/compliance/gdpr-center/"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                Garanties RGPD AWS
              </a>{' '}
              et{' '}
              <a
                href="https://aws.amazon.com/compliance/sub-processors/"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                sous-traitants AWS
              </a>
            </li>
            <li>
              <a
                href="https://cloud.google.com/terms/data-processing-addendum"
                target="_blank"
                rel="noreferrer"
                className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
              >
                Cloud Data Processing Addendum (Google Cloud)
              </a>
            </li>
          </ul>
        </details>
        <p>
          Pour obtenir une copie des garanties applicables à son utilisation du
          service, le client peut écrire à l&apos;adresse indiquée ci-dessous.
        </p>
      </LegalSection>

      <LegalSection title="Stockage du navigateur et traceurs">
        <p>
          Les routes publiques de cette version n&apos;installent pas de cookie.
          Elles utilisent le stockage local pour la langue et l&apos;état de
          connexion, ainsi que le stockage de session pour le contexte de
          marque. Après connexion, les jetons nécessaires à la session,
          l&apos;organisation sélectionnée et certaines préférences
          d&apos;affichage sont également conservés dans le navigateur.
        </p>
        <p>
          Aucun outil publicitaire ou de mesure d&apos;audience n&apos;a été
          identifié sur ces routes publiques ; aucune bannière de consentement
          n&apos;est donc affichée. Les fonctions authentifiées peuvent appeler
          les prestataires nécessaires à l&apos;action choisie. L&apos;ajout
          futur d&apos;un traceur non essentiel imposera une information et,
          lorsque la loi le demande, un consentement préalable aussi simple à
          refuser qu&apos;à accepter.
        </p>
      </LegalSection>

      <LegalSection title="Sécurité">
        <p>
          L&apos;application utilise HTTPS pour les échanges réseau et met en
          œuvre des mécanismes d&apos;authentification et de contrôle des accès.
          Cette page n&apos;affirme pas un chiffrement particulier au repos, une
          surveillance continue ou un plan de reprise tant que ces engagements
          ne sont pas rattachés à une preuve opérationnelle vérifiée.
        </p>
      </LegalSection>

      <LegalSection title="Vos droits">
        <p>
          Selon le traitement, vous pouvez demander l&apos;accès, la
          rectification, l&apos;effacement, la limitation, l&apos;opposition ou
          la portabilité de vos données. Lorsque la demande concerne un dossier
          RH ou de paie, elle peut devoir être transmise à l&apos;employeur
          responsable du dossier.
        </p>
        <p>
          Adressez votre demande à{' '}
          <a
            href={`mailto:${LEGAL_SERVICE.contactEmail}`}
            className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
          >
            {LEGAL_SERVICE.contactEmail}
          </a>
          . Une preuve d&apos;identité ne sera demandée que si elle est
          nécessaire pour éviter de communiquer les données à la mauvaise
          personne. Vous pouvez également déposer une réclamation auprès de la{' '}
          <a
            href="https://www.cnil.fr/fr/plaintes"
            target="_blank"
            rel="noreferrer"
            className="font-medium underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950"
          >
            CNIL
          </a>
          .
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
